import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Zap, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function CENLogo({ size = 48, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" className={className}>
      <ellipse cx="20" cy="20" rx="17" ry="7" stroke="currentColor" strokeWidth="1.5" transform="rotate(-30 20 20)" />
      <ellipse cx="20" cy="20" rx="17" ry="7" stroke="currentColor" strokeWidth="1.5" transform="rotate(30 20 20)" />
      <ellipse cx="20" cy="20" rx="17" ry="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M21.5 13L16 21.5H20.5L18.5 27L24 18.5H19.5L21.5 13Z" fill="currentColor" />
    </svg>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const homeFor = (u) => {
    if (u?.role === "Proyectista") return "/ingenieria/proyecto";
    if (u?.role === "Residente de Obra") return "/compras/materiales";
    if (u?.role === "Operario Electricista") return "/compras/materiales";
    return "/dashboard";
  };

  useEffect(() => {
    if (user) navigate(homeFor(user), { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Por favor ingrese email y contrasena");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const loggedUser = await login(email, password);
      window.__cenShowWelcome = true;
      navigate(homeFor(loggedUser), { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Error al iniciar sesion. Verifique sus credenciales.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex">
      {/* Left panel - branding */}
      <div
        className="hidden lg:flex lg:w-[46%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: "linear-gradient(160deg, #132F4F 0%, #1A4A7A 40%, #2E6DA4 100%)" }}
      >
        {/* Background pattern */}
        <div className="absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full border border-white/[0.06]"
              style={{
                width: `${200 + i * 100}px`,
                height: `${200 + i * 100}px`,
                top: "55%",
                left: "45%",
                transform: `translate(-50%, -50%)`,
              }}
            />
          ))}
          {/* Decorative dots */}
          <div className="absolute top-[20%] left-[15%] w-1.5 h-1.5 rounded-full bg-blue-300/30" />
          <div className="absolute top-[40%] right-[10%] w-2 h-2 rounded-full bg-amber-300/20" />
          <div className="absolute bottom-[30%] left-[25%] w-1 h-1 rounded-full bg-white/20" />
        </div>

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/10">
            <Zap size={20} className="text-white" />
          </div>
          <div>
            <p className="text-white font-bold text-xl tracking-tight">CEN</p>
            <p className="text-blue-300/70 text-[10px] font-semibold tracking-[0.2em] uppercase">Ingenieria E.A.S.</p>
          </div>
        </div>

        {/* Center content */}
        <div className="relative z-10 max-w-md">
          <h1 className="text-white font-bold text-4xl leading-[1.15] mb-4 tracking-tight">
            Sistema ERP<br />
            <span className="text-blue-200/80">Integrado</span>
          </h1>
          <p className="text-blue-100/60 text-base leading-relaxed mb-10">
            Gestion completa de proyectos electricos, finanzas, inventario y personal en una sola plataforma.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Proyectos Activos", value: "4" },
              { label: "Trabajadores", value: "80+" },
              { label: "Modulos ERP", value: "7" },
              { label: "En Operacion", value: "2026" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white/[0.06] backdrop-blur-sm rounded-xl p-4 border border-white/[0.08] hover:bg-white/[0.09] transition-colors">
                <p className="text-white font-bold text-2xl tracking-tight">{stat.value}</p>
                <p className="text-blue-200/50 text-[12px] font-medium mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-blue-200/40 text-sm font-medium">CEN Ingenieria E.A.S. &mdash; Paraguay</p>
          <p className="text-blue-300/25 text-xs mt-1">ERP v1.0</p>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-12">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-xl bg-[#2E6DA4] flex items-center justify-center shadow-md">
            <Zap size={20} className="text-white" />
          </div>
          <div>
            <p className="text-gray-900 font-bold text-lg tracking-tight">CEN Ingenieria</p>
            <p className="text-gray-400 text-[10px] font-semibold uppercase tracking-[0.15em]">ERP System</p>
          </div>
        </div>

        <div className="w-full max-w-[400px]">
          <div className="mb-8">
            <h2 className="text-[28px] font-bold text-gray-900 tracking-tight">Iniciar Sesion</h2>
            <p className="text-gray-400 mt-1.5 text-[15px]">Ingrese sus credenciales para acceder</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                Correo Electronico
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@cen.com.py"
                autoComplete="email"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-300 focus:ring-2 focus:ring-[#2E6DA4]/15 focus:border-[#2E6DA4] transition-all bg-white text-[14px]"
                data-testid="email-input"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                Contrasena
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingrese su contrasena"
                  autoComplete="current-password"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 pr-11 text-gray-900 placeholder-gray-300 focus:ring-2 focus:ring-[#2E6DA4]/15 focus:border-[#2E6DA4] transition-all bg-white text-[14px]"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div
                className="flex items-start gap-2.5 bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-[13px] font-medium"
                data-testid="login-error"
              >
                <AlertTriangleIcon />
                <span>{error}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white font-semibold py-3.5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2.5 disabled:opacity-60 disabled:cursor-not-allowed shadow-md shadow-[#2E6DA4]/15 text-[14px] group"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={17} className="animate-spin" />
                  Iniciando sesion...
                </>
              ) : (
                <>
                  Iniciar Sesion
                  <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Security notice */}
          <div className="mt-8 p-4 bg-gray-50/80 border border-gray-100 rounded-xl">
            <p className="text-[11px] text-gray-400 text-center leading-relaxed">
              Acceso restringido a personal autorizado. Si no tiene credenciales, contacte al administrador del sistema.
            </p>
          </div>
        </div>

        <p className="mt-10 text-[11px] text-gray-300 text-center font-medium">
          CEN Ingenieria E.A.S. &bull; Sistema ERP Interno
        </p>
      </div>
    </div>
  );
}

function AlertTriangleIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <path d="M12 9v4" /><path d="M12 17h.01" />
    </svg>
  );
}
